import clsx from 'clsx';
import s from './ProductCard.module.scss';
import sg from '../../styles/global.module.scss';
import { Product } from '../../models/product';
import CardItem from '../CardItem/CardItem';
import MediaSwiper from '../MediaSwiper/MediaSwiper';
import React, { useMemo, useState } from 'react';
import BuyBlock from '../BuyBlock/BuyBlock';
import { IS_INSTAGRAM, IS_IOS_NATIVE, IS_SAFARI } from '../../utils/browser';
import { formatDistance, getCategoryAndSubcategoriesLine } from './helpers';
import { useLocation, useRouteMatch } from 'react-router';
import {
  AnalyticEventsEnum,
  getProductAnalyticData,
  trackEvent,
} from '../../Analytics';
import { getSeoApproximateLocation, getSeoLocation } from '../Location/config';
import { useIonRouter } from '@ionic/react';

export interface ProductCardProps {
  product: Product;
  disableSwipeOnTouch?: boolean;
  showCategory?: boolean;
}

export const getProductDetailUrl = (product: Product) => {
  return `/catalog/${getSeoApproximateLocation(product.location)}/${
    product.id
  }`;
};

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  disableSwipeOnTouch,
  showCategory,
}) => {
  const location = useLocation();
  const isBusinessPlatform = location.pathname.startsWith('/business');

  const [activeIndex, setActiveIndex] = useState<number>();

  const subtitle = useMemo(
    () => getCategoryAndSubcategoriesLine(product, showCategory),
    [product, showCategory]
  );

  const moreHeight = IS_SAFARI || IS_INSTAGRAM || IS_IOS_NATIVE;

  const match = useRouteMatch();
  const matchUrl = match.url.replace(/\/$/, '');

  const distance = (product as any).extraDistance;
  const distanceString =
    distance !== null
      ? `${formatDistance(distance).toLocaleString()} km away`
      : '';

  const locationString =
    // For Bali we do not show city
    // KEEP SYNC: getSeoLocation
    (product.location.state !== 'Bali' ? product.location.city : null) ||
    product.location.state ||
    product.location.country;

  const router = useIonRouter();

  return (
    <CardItem
      flex // <---- flexbox wrapper
      onClick={async e => {
        e.preventDefault();
        e.stopPropagation();

        if (isBusinessPlatform) {
          router.push(`${matchUrl}/edit/${product.id}`, 'forward', 'push');
          return;
        }

        router.push(getProductDetailUrl(product), 'forward', 'push');

        const analyticData = getProductAnalyticData(product);
        await trackEvent(AnalyticEventsEnum.CLICK__PRODUCT, {
          ...analyticData,
        });
      }}
    >
      <div
        className={clsx(s.Image__container)}
        style={{ flex: 1 }} // <---- fill all available space
      >
        <MediaSwiper
          isPreview // <---- absolute position
          activeIndex={activeIndex}
          setActiveIndex={setActiveIndex}
          disableSwipeOnTouch={disableSwipeOnTouch}
          uploadedFiles={product.uploadedFiles}
        />
      </div>

      <div
        className={clsx(sg.Stack_Vertical, sg.Gap_10, sg.Width_100)}
        style={{
          height: moreHeight ? '110px' : '100px', // <---- fixed height
        }}
      >
        <div className={clsx(sg.Stack_Horizontal)}>
          <div className={clsx(sg.Stack_Vertical, sg.Gap_5, s.Text_Container)}>
            <p className={clsx(s.Text_Title)}>{product.title}</p>
            <p className={clsx(s.Text_Subtitle)}>
              {locationString} - {distanceString}
            </p>
          </div>
        </div>

        <BuyBlock
          isPreview
          priceFrom={product.priceFrom}
          priceTo={product.priceTo}
          priceCurrency={product.priceCurrency}
          priceType={product.priceType}
          contactInfoContextData={{
            product,
          }}
        />
      </div>
    </CardItem>
  );
};

export default ProductCard;
